// « Nouvel éditeur mobile » — THE ONE SWITCH (2026-10-19).
//
// The new phone layout re-arranges the EXISTING editor components (top bar,
// tab bar, bottom sheets); it ships OFF. It is on only when BOTH hold:
//
//   1. the screen is a phone — narrower than a tablet in portrait, or a phone
//      turned sideways (short and not tablet-wide). Tablets and desktops never
//      get it: the desktop editor must not change at all.
//   2. somebody asked for it — a local override (`?m2=1` in the URL, which
//      sticks in localStorage `sz-m2` so a reload keeps it; `?m2=0` clears it),
//      or the future `app_settings.mobile_editor_v2` flag (migration 257,
//      written, NOT applied — until then the settings API never sends it and
//      this reads `undefined` = off).
//
// Everything else in the editor asks `useMobileEditorV2()`; nothing tests the
// width or the flag on its own, so switching it off is exactly the old editor.
import { useEffect, useState } from 'react';

export const M2_STORAGE_KEY = 'sz-m2';
export const M2_QUERY_PARAM = 'm2';

/** Phone = narrower than a portrait tablet, or a phone held sideways. */
export const PHONE_MEDIA_QUERY = '(max-width: 767px), (max-height: 500px) and (max-width: 1000px)';

/** Reads (and remembers) the local override. `?m2=1` → on and stored,
 *  `?m2=0` → off and forgotten; no parameter → whatever was stored. */
export function readMobileEditorV2Override(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null = safeStorage(),
): boolean {
  let param: string | null = null;
  try { param = new URLSearchParams(search).get(M2_QUERY_PARAM); } catch { /* bad query */ }
  if (param === '1' || param === 'true') {
    try { storage?.setItem(M2_STORAGE_KEY, '1'); } catch { /* private mode */ }
    return true;
  }
  if (param === '0' || param === 'false') {
    try { storage?.removeItem(M2_STORAGE_KEY); } catch { /* private mode */ }
    return false;
  }
  try { return storage?.getItem(M2_STORAGE_KEY) === '1'; } catch { return false; }
}

/** The decision itself, pure (unit-tested). */
export function decideMobileEditorV2(input: { isPhone: boolean; override: boolean; settingsFlag?: boolean | null }): boolean {
  return input.isPhone && (input.override || input.settingsFlag === true);
}

function safeStorage(): Storage | null {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
}

function matchesPhone(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(PHONE_MEDIA_QUERY).matches;
}

/** True when the editor should draw the new phone layout. Re-evaluates on
 *  rotation / resize (a phone turned sideways stays a phone). */
export function useMobileEditorV2(settingsFlag?: boolean | null): boolean {
  const [override] = useState(() => readMobileEditorV2Override());
  const [isPhone, setIsPhone] = useState(matchesPhone);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(PHONE_MEDIA_QUERY);
    const update = () => setIsPhone(mql.matches);
    update();
    mql.addEventListener?.('change', update);
    return () => mql.removeEventListener?.('change', update);
  }, []);
  return decideMobileEditorV2({ isPhone, override, settingsFlag });
}

// ─── View-only albums ──────────────────────────────────────────────────────
// The SERVER's lock (worker/src/routes/sessions.ts, POST /sessions): once
// printing has started, any save from someone who is not role 'admin' answers
// 423. The classic editor only showed a banner and let every edit happen on
// screen, then threw it away (audit B1). The new layout reads THIS list, so
// what the screen allows is exactly what the server keeps. A guard test
// checks it against the worker's list.
export const EDIT_LOCKED_STATUSES = [
  'ready-to-print', 'in-production', 'printed', 'ready-to-deliver', 'shipped', 'delivered',
] as const;

export function isViewOnlyAlbum(status: string | null | undefined, role: string | null | undefined): boolean {
  if (!status || role === 'admin') return false;
  return (EDIT_LOCKED_STATUSES as readonly string[]).includes(status);
}
